import { CreateAccountFromRequestModal } from '@app/components/accounts/CreateAccount/CreateAccountFromRequestModal';
import { ListFilter } from '@app/components/ListFilter';
import { EmptyState } from '@app/components/various/EmptyState';
import { PageTitle } from '@app/components/various/PageTitle';
import { Pagination } from '@app/components/various/Pagination';
import { RequestStatus } from '@app/components/various/RequestStatus';
import { RequestStatusCell, Table } from '@app/components/various/Table';
import { ClickableRowCell } from '@app/components/various/Table/Cells/ClickableRowCell';
import { EmailWithLinkCell } from '@app/components/various/Table/Cells/EmailWithLinkCell';
import { TableControls } from '@app/components/various/TableControls';
import { AccountRequestStatus } from '@app/constants/requests';
import { AccountRequestsActionCell } from '@app/pages/AccountRequestsList/AccountRequestsActionCell';
import { AccountRequestsListCard } from '@app/pages/AccountRequestsList/AccountRequestsListCard';
import { AccountRequestsListCardSkeleton } from '@app/pages/AccountRequestsList/AccountRequestsListCardSkeleton';
import { compiledPaths } from '@app/paths';
import { ACCOUNT_REQUESTS_QUERY_KEY, useAccountRequests } from '@app/services/hooks/account/useAccountRequests';
import { useAccountRequestStatus } from '@app/services/hooks/account/useAccountRequestStatus';
import { useConfirmationGuard, usePageScrolling, useUrlPagination, useUrlState } from '@app/utils/hooks';
import { usePaginationHandler } from '@app/utils/hooks/table/usePaginationHandler';
import { useTable } from '@app/utils/hooks/table/useTable';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { useAccountRequestStatusLabel } from '@app/utils/hooks/useAccountRequestStatusCell';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { isDefined } from '@app/utils/is';
import { renderSentryMaskedText } from '@app/utils/monitoring';
import { getAccountRequestsStatusColor } from '@app/utils/status';
import { ACTION_COLUMN_WIDTH, renderStandardCell } from '@app/utils/table';
import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { IconComponentProps, Lookbook } from '@typings';
import cx from 'classnames';
import { isEmpty } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as ReactLink, useLocation } from 'react-router-dom';

const columnHelper = createColumnHelper<Lookbook.AccountRequestListing>();

interface ChangeStatusParams {
  accountRequestId: string;
  companyName: string;
}

export const AccountRequestsList = () => {
  const { t } = useTranslation(['common', 'accounts', 'requests']);
  const [statusFilter, setStatusFilter] = useUrlState('status');
  const [page, setPage] = useUrlPagination();
  const confirmationGuard = useConfirmationGuard();
  const { mutate: updateAccountRequestStatus } = useAccountRequestStatus();
  const { showModal: showCreateAccountModal, isOpen: isShowCreateAccountModalOpen, hideModal: hideCreateAccountModal } = useModalToggle();
  const queryClient = useQueryClient();
  const { scrollToTopDelay } = usePageScrolling();
  const location = useLocation();

  const [selectedAccountRequestId, setSelectedAccountRequestId] = React.useState<string | undefined>();

  const handleHideCreateAccountModal = () => {
    setSelectedAccountRequestId(undefined);
    hideCreateAccountModal();
  };

  const handleSuccessCreateAccountModal = () => {
    setSelectedAccountRequestId(undefined);
    hideCreateAccountModal();
  };

  const handleOpenCreateAccountRequestModal = React.useCallback(
    (id: string) => {
      setSelectedAccountRequestId(id);
      showCreateAccountModal();
    },
    [showCreateAccountModal],
  );

  const handleSetAccountRequestToPending = React.useCallback(
    ({ accountRequestId, companyName }: ChangeStatusParams) => {
      updateAccountRequestStatus(
        { companyName, id: accountRequestId, status: AccountRequestStatus.Pending },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_REQUESTS_QUERY_KEY, { page, status: statusFilter }] });
          },
        },
      );
    },
    [page, queryClient, statusFilter, updateAccountRequestStatus],
  );

  const handleOpenDeclineAccountRequestModal = React.useCallback(
    ({ accountRequestId, companyName }: ChangeStatusParams) => {
      confirmationGuard(() => ({
        content: t('requests:modal_decline_request.description'),
        okText: t('accounts:decline'),
        onOk: () =>
          isDefined(accountRequestId) &&
          updateAccountRequestStatus(
            { companyName, id: accountRequestId, status: AccountRequestStatus.Declined },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [ACCOUNT_REQUESTS_QUERY_KEY, { page, status: statusFilter }] });
              },
            },
          ),
        title: t('requests:modal_decline_request.title'),
      }))();
    },
    [confirmationGuard, page, queryClient, statusFilter, t, updateAccountRequestStatus],
  );

  const handleOpenArchiveAccountRequestModal = React.useCallback(
    ({ accountRequestId, companyName }: ChangeStatusParams) => {
      confirmationGuard(() => ({
        content: t('requests:modal_archive_request.description'),
        okText: t('requests:archive'),
        onOk: () =>
          isDefined(accountRequestId) &&
          updateAccountRequestStatus(
            { companyName, id: accountRequestId, status: AccountRequestStatus.Archived },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [ACCOUNT_REQUESTS_QUERY_KEY, { page, status: statusFilter }] });
              },
            },
          ),
        title: t('requests:modal_archive_request.title'),
      }))();
    },
    [confirmationGuard, page, queryClient, statusFilter, t, updateAccountRequestStatus],
  );

  const { data: accountRequestsData, isPending } = useAccountRequests({ page, status: statusFilter });
  const accountRequests = React.useMemo(() => accountRequestsData?.data ?? [], [accountRequestsData?.data]);
  const wishlistsTotal = accountRequestsData?.totalCount ?? 0;

  const { getStatusLabel } = useAccountRequestStatusLabel();
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'accountRequests' });

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('status', {
        cell: cell => <RequestStatusCell status={cell.row.original.status} tooltip={getStatusLabel(cell.row.original.status)} />,
        header: t('common:status'),
        meta: {
          align: 'center',
          noWrap: true,
          width: 50,
        },
      }),
      columnHelper.accessor('company', {
        cell: info => (
          <ClickableRowCell
            path={compiledPaths.ACCOUNT_REQUEST_DETAILS({ accountRequestId: info.row.original.id })}
            text={info.row.original.company}
            state={{ from: location }}
          />
        ),
        enableHiding: false,
        header: t('common:company_name'),
      }),
      columnHelper.accessor('email', {
        cell: info => <EmailWithLinkCell path={`mailto:${info.row.original.email}`} email={info.row.original.email} />,
        header: t('accounts:contact'),
      }),
      columnHelper.display({
        cell: ({ row }) => renderSentryMaskedText(`${row.original.firstName} ${row.original.lastName}`),
        header: t('common:name'),
        id: 'fullName',
      }),
      columnHelper.accessor('source.name', {
        cell: cell => {
          if (isDefined(cell.row.original.source?.data.lookbookHash)) {
            return (
              <ReactLink
                data-sentry-mask
                className="rounded-small underline body-1 focus-visible:focus-outline"
                to={compiledPaths.LOOKBOOK_SINGLE({ lookbookId: cell.row.original.source.data.lookbookHash })}
              >
                {cell.getValue()}
              </ReactLink>
            );
          }

          return renderStandardCell(cell);
        },
        header: t('accounts:source'),
        id: 'sourceName',
      }),
      columnHelper.accessor('createdAt', {
        cell: ({ row, getValue }) =>
          isDefined(row.original.source?.data.lookbookHash) ?
            <ReactLink
              className="rounded-small body-1 focus-visible:focus-outline"
              to={compiledPaths.LOOKBOOK_SINGLE({ lookbookId: row.original.source.data.lookbookHash })}
            >
              {getValue()}
            </ReactLink>
          : getValue(),
        header: t('requests:requested'),
      }),
      columnHelper.display({
        cell: info => {
          const { id, company } = info.row.original;

          return (
            <AccountRequestsActionCell
              accountRequestStatus={info.row.original.status}
              onSetToPending={() => handleSetAccountRequestToPending({ accountRequestId: id, companyName: company })}
              openCreateModal={() => handleOpenCreateAccountRequestModal(id)}
              openDeclineModal={() => handleOpenDeclineAccountRequestModal({ accountRequestId: id, companyName: company })}
              openArchiveModal={() => handleOpenArchiveAccountRequestModal({ accountRequestId: id, companyName: company })}
            />
          );
        },
        enableHiding: false,
        id: 'accountRequest',
        meta: {
          skeletonType: 'actions',
          width: ACTION_COLUMN_WIDTH,
        },
      }),
    ],
    [
      t,
      getStatusLabel,
      location,
      handleSetAccountRequestToPending,
      handleOpenCreateAccountRequestModal,
      handleOpenDeclineAccountRequestModal,
      handleOpenArchiveAccountRequestModal,
    ],
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    scrollToTopDelay();
  };

  React.useEffect(() => {
    if (!isPending && page > 1 && isEmpty(accountRequests)) {
      setPage(page - 1);
    }
  }, [accountRequests, isPending, page, setPage]);

  const { pagination, handlePaginationChange } = usePaginationHandler({ onPageChange: handlePageChange, page });

  const shouldShowTable = isPending || !isEmpty(accountRequests);

  const statuses = React.useMemo(
    () => [
      { name: t('common:all'), value: '' },
      { name: t('accounts:pending'), value: '0' },
      { name: t('accounts:archived'), value: '3' },
      { name: t('accounts:declined'), value: '2' },
      { name: t('accounts:approved'), value: '1' },
    ],
    [t],
  );

  const filters = React.useMemo(() => {
    return statuses.map(stage => ({
      icon: ({ className }: IconComponentProps) => (
        <span className={cx(className, 'flex items-center justify-center')}>
          {!isEmpty(stage.value) && <RequestStatus type={parseInt(stage.value) as AccountRequestStatus} />}
        </span>
      ),
      ...stage,
    }));
  }, [statuses]);

  const table = useTable(
    {
      columns,
      data: accountRequests,
      enableSorting: false,
      getCoreRowModel: getCoreRowModel(),
      manualPagination: true,
      onPaginationChange: handlePaginationChange,
      state: {
        pagination,
      },
    },
    columnsVisibility,
  );

  return (
    <PageTitle title={t('requests:requests')}>
      <div className="px-4 lg:px-8">
        <div className="mb-2 flex items-center justify-start gap-2 py-2 lg:mb-4">
          <h2 className="h2">
            {t('requests:requests')} {!isPending && `(${wishlistsTotal})`}
          </h2>
          <div className="flex items-center gap-2 pl-8">
            <ListFilter title={t('requests:select_request_status')} onChange={setStatusFilter} selected={statusFilter} filters={filters} />
            <TableControls table={table} columnsConfiguration={columnsVisibility} tableName="accountRequests" />
          </div>
        </div>
        {shouldShowTable ?
          <>
            <Table
              table={table}
              className="!grid-cols-1"
              isLoading={isPending}
              getRowMarkerColor={row => getAccountRequestsStatusColor(row.original.status)}
              renderCard={(row, index) => {
                const { id, company } = row.original;

                return (
                  <AccountRequestsListCard
                    key={index}
                    row={row}
                    onSetToPending={() => handleSetAccountRequestToPending({ accountRequestId: id, companyName: company })}
                    openCreateModal={() => handleOpenCreateAccountRequestModal(id)}
                    openDeclineModal={() => handleOpenDeclineAccountRequestModal({ accountRequestId: id, companyName: company })}
                    openArchiveModal={() => handleOpenArchiveAccountRequestModal({ accountRequestId: id, companyName: company })}
                  />
                );
              }}
              renderSkeletonCard={index => <AccountRequestsListCardSkeleton key={index} />}
            />
            <Pagination onChange={table.setPageIndex} current={page} totalItems={wishlistsTotal} />
          </>
        : <EmptyState title={t('requests:no_results')} />}
      </div>
      <CreateAccountFromRequestModal
        isOpen={isShowCreateAccountModalOpen}
        onClose={handleHideCreateAccountModal}
        onSuccess={handleSuccessCreateAccountModal}
        accountRequestId={selectedAccountRequestId}
        createSource="lookbook-request"
      />
    </PageTitle>
  );
};
